import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { matchesContractSymbol, matchesOrderType, pairMatchesContractFilter, settlementMatchesContractCode } from "./orderFilterHelpers";
import {
  extractFuturesOrders,
  mapFuturesOpenOrderRow,
  matchesFuturesOrderType,
  toFuturesApiSymbol,
} from "./futuresProfileOrderHelpers";

const TYPE_OPTIONS = [
  { value: "limit", label: "Limit" },
  { value: "market", label: "Market" },
  { value: "stop_limit", label: "Stop Limit" },
  { value: "stop_market", label: "Stop Market" },
];

const FUTURES_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "LIMIT", label: "Limit" },
  { value: "MARKET", label: "Market" },
  { value: "CONDITIONAL", label: "Conditional" },
];

const SETTLEMENT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "usdt", label: "USDT" },
  { value: "usdc", label: "USDC" },
  { value: "busd", label: "BUSD" },
];

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btcusdt", label: "BTCUSDT" },
  { value: "ethusdt", label: "ETHUSDT" },
  { value: "bnbusdt", label: "BNBUSDT" },
];

const ORDER_DESKTOP_COL_COUNT = 11;

/*
const OPEN_ORDER_ROWS = Array.from({ length: 8 }, (_, i) => ({
  id: i + 1,
  contract: "BTCUSDT",
  orderId: "26475839201",
  creationTime: "2026-04-16 10:30:15",
  side: "Buy",
  price: "68,450.00",
  size: "0.5000",
  unfilled: "0.5000",
  fillPrice: "--",
  source: "Web",
  remarks: "Open Long Position",
}));
*/
const OPEN_ORDER_ROWS = [];
const FETCH_LIMIT = 100;

const OpenOrders = ({ pageTitle = "Open Orders", idPrefix = "oo" }) => {
  const isFutures = String(pageTitle || "").toLowerCase().includes("futures") || idPrefix === "fo";
  const defaultType = isFutures ? "all" : "limit";
  const [filterType, setFilterType] = useState(defaultType);
  const [settlement, setSettlement] = useState("all");
  const [contract, setContract] = useState("all");
  const [filterTypeDraft, setFilterTypeDraft] = useState(defaultType);
  const [settlementDraft, setSettlementDraft] = useState("all");
  const [contractDraft, setContractDraft] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [futuresRows, setFuturesRows] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);
  const titleId = `${idPrefix}-page-title`;
  const typeOptions = isFutures ? FUTURES_TYPE_OPTIONS : TYPE_OPTIONS;

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const loadFuturesOpenOrders = useCallback(async () => {
    if (!isFutures) return;
    try {
      const res = await AuthService.futuresOpenOrders({
        symbol: toFuturesApiSymbol(contract),
        skip: 0,
        limit: FETCH_LIMIT,
      });
      const mapped = extractFuturesOrders(res).map(mapFuturesOpenOrderRow);
      setFuturesRows(mapped);
    } catch {
      setFuturesRows([]);
    }
  }, [isFutures, contract]);

  useEffect(() => {
    void loadFuturesOpenOrders();
  }, [loadFuturesOpenOrders]);

  const handleCancelOrder = async (orderId) => {
    if (!orderId || cancellingId) return;
    setCancellingId(orderId);
    try {
      const result = await AuthService.futuresCancelOrder(orderId);
      if (result?.success) {
        alertSuccessMessage("Order Cancelled");
        await loadFuturesOpenOrders();
      } else {
        alertErrorMessage(result?.error?.message || result?.message || "Failed to cancel order");
      }
    } catch {
      alertErrorMessage("Something went wrong");
    } finally {
      setCancellingId(null);
    }
  };

  const filteredRows = useMemo(() => {
    if (isFutures) {
      return futuresRows.filter((row) => {
        if (!matchesFuturesOrderType(row.orderType, filterType)) return false;
        if (!pairMatchesContractFilter(row.rawSymbol || row.contract, contract)) return false;
        if (!settlementMatchesContractCode(row.rawSymbol || row.contract, settlement)) return false;
        return true;
      });
    }
    return OPEN_ORDER_ROWS.filter((row) => {
      if (!matchesOrderType(row.orderType, filterType)) return false;
      if (!matchesContractSymbol(row.contract, contract)) return false;
      if (settlement && settlement !== "all") {
        const suf = settlement.toUpperCase();
        if (!String(row.contract || "").toUpperCase().endsWith(suf)) return false;
      }
      return true;
    });
  }, [isFutures, futuresRows, filterType, settlement, contract]);

  const resetFilters = () => {
    const defaultType = isFutures ? "all" : "limit";
    setFilterType(defaultType);
    setSettlement("all");
    setContract("all");
    setFilterTypeDraft(defaultType);
    setSettlementDraft("all");
    setContractDraft("all");
  };

  const applyMobileFilters = () => {
    setFilterType(filterTypeDraft);
    setSettlement(settlementDraft);
    setContract(contractDraft);
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const isMob = keyPrefix === "mob";
    const ft = isMob ? filterTypeDraft : filterType;
    const st = isMob ? settlementDraft : settlement;
    const ct = isMob ? contractDraft : contract;
    const setFt = isMob ? setFilterTypeDraft : setFilterType;
    const setSt = isMob ? setSettlementDraft : setSettlement;
    const setCt = isMob ? setContractDraft : setContract;
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter open orders">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}${idPrefix}-dd-type`}
          label="Type"
          value={ft}
          options={typeOptions}
          onChange={setFt}
          menuKey={`${pref}${idPrefix}-type`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}${idPrefix}-dd-settlement`}
          label="Settlement Coin"
          value={st}
          options={SETTLEMENT_OPTIONS}
          onChange={setSt}
          menuKey={`${pref}${idPrefix}-settlement`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}${idPrefix}-dd-contract`}
          label="Contract"
          value={ct}
          options={CONTRACT_OPTIONS}
          onChange={setCt}
          menuKey={`${pref}${idPrefix}-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={11} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby={titleId}>
            <h1 id={titleId} className="so-page__title">
              {pageTitle}
            </h1>

            {isFutures ? (
              <OrdersSubTabs
                tabs={[
                  { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
                  { label: "Order History", to: "/user_profile/orders/futures_order_history" },
                  { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
                  { label: "Position History", to: "/user_profile/orders/position_history" },
                ]}
              />
            ) : null}

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap">
              <table className="so-table so-table--air">
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Order ID</th>
                    <th scope="col">Creation Time</th>
                    <th scope="col" className="so-table__th--center">
                      Side
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Price
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Size
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Unfilled
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Fill Price
                    </th>
                    <th scope="col">Source</th>
                    <th scope="col">Remarks</th>
                    <th scope="col" className="so-table__th--center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : filteredRows.map((row) => {
                    const cancelDisabled = cancellingId === row.id;
                    return (
                    <tr key={row.id}>
                      <td>
                        <div>{row.contract}</div>
                        {isFutures && row.leverage != null ? (
                          <div className="small text-muted">{row.leverage}x · {row.marginMode}</div>
                        ) : null}
                      </td>
                      <td className="so-num">{row.orderId}</td>
                      <td>{row.creationTime}</td>
                      <td className="so-table__td--center">
                        <span className={row.side === "Buy" ? "so-side--buy" : "so-side--sell"}>{row.side}</span>
                      </td>
                      <td className="so-table__td--right so-num">
                        <div>{row.price}</div>
                        {row.priceSubtext ? <div className="small text-muted">{row.priceSubtext}</div> : null}
                      </td>
                      <td className="so-table__td--right so-num">
                        {row.size}{isFutures && row.baseAsset ? ` ${row.baseAsset}` : ""}
                      </td>
                      <td className="so-table__td--right so-num">
                        {row.unfilled}{isFutures && row.baseAsset ? ` ${row.baseAsset}` : ""}
                      </td>
                      <td className="so-table__td--right so-num so-table__td--muted">{row.fillPrice}</td>
                      <td>{isFutures ? (row.orderTypeLabel || row.orderType) : row.source}</td>
                      <td>{row.remarks}</td>
                      <td className="so-table__td--center">
                        {isFutures ? (
                          <button
                            type="button"
                            className="so-cancelLink"
                            disabled={cancelDisabled}
                            onClick={() => handleCancelOrder(row.id)}
                          >
                            {cancelDisabled ? "Cancelling…" : "Cancel"}
                          </button>
                        ) : (
                          <button type="button" className="so-cancelLink">Cancel</button>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">{pageTitle}</h2>
          {isFutures ? (
            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
                { label: "Order History", to: "/user_profile/orders/futures_order_history" },
                { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
                { label: "Position History", to: "/user_profile/orders/position_history" },
              ]}
            />
          ) : null}
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-open-orders-filters"
              toolbar={renderToolbar(true, "mob")}
              onSearch={applyMobileFilters}
              onReset={resetFilters}
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map((row) => {
              const cancelDisabled = cancellingId === row.id;
              return (
            <OrderMobileCardShell
              key={`m-${row.id}`}
              desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
              popupTitle={`${pageTitle} details`}
              heroPrimary={row.contract}
              heroSecondary={isFutures ? `${row.side} · ${row.orderTypeLabel || row.orderType}` : undefined}
            >
              <div className="so-mobileCard__row">
                <span>Order ID</span>
                <span className="so-num">{row.orderId}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Creation Time</span>
                <span>{row.creationTime}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Side</span>
                <span className={row.side === "Buy" ? "so-side--buy" : "so-side--sell"}>{row.side}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Price</span>
                <span className="so-num">{row.price}</span>
              </div>
              {row.priceSubtext ? (
                <div className="so-mobileCard__row">
                  <span>Trigger</span>
                  <span className="so-num">{row.priceSubtext}</span>
                </div>
              ) : null}
              <div className="so-mobileCard__row">
                <span>Size</span>
                <span className="so-num">{row.size}{isFutures && row.baseAsset ? ` ${row.baseAsset}` : ""}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Unfilled</span>
                <span className="so-num">{row.unfilled}{isFutures && row.baseAsset ? ` ${row.baseAsset}` : ""}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Fill Price</span>
                <span className="so-num so-table__td--muted">{row.fillPrice}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>{isFutures ? "Type" : "Source"}</span>
                <span>{isFutures ? (row.orderTypeLabel || row.orderType) : row.source}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Remarks</span>
                <span>{row.remarks}</span>
              </div>
              {isFutures ? (
                <div className="so-mobileCard__row">
                  <span>Status</span>
                  <span>{row.status}</span>
                </div>
              ) : null}
              <div className="so-mobileCard__actions so-mobileCard__actions--split">
                <span className="so-mobileCard__statusLabel">{isFutures ? "Action" : "Status"}</span>
                <button
                  type="button"
                  className="so-cancelLink"
                  disabled={isFutures && cancelDisabled}
                  onClick={() => (isFutures ? handleCancelOrder(row.id) : undefined)}
                >
                  {isFutures && cancelDisabled ? "Cancelling…" : "Cancel"}
                </button>
              </div>
            </OrderMobileCardShell>
          );})
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenOrders;

